import type { SxProps, Theme } from '@mui/material';
import { Box } from '../../Layout/Box';
import { IconButton } from '../../Buttons';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { forwardRef } from 'react';

export interface PaginationProps {
  /**
   * The current active page (1-indexed). Must be a positive integer in range [1, totalPages].
   */
  currentPage: number;
  /**
   * The total number of pages. Must be a positive integer (>= 1).
   */
  totalPages: number;
  /**
   * Callback fired when the page changes.
   * @param page - The new page number (1-indexed)
   */
  onPageChange: (page: number) => void;
  /**
   * Size of the navigation buttons.
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * The data-testid attribute for testing.
   */
  'data-testid'?: string;
}

/**
 * Validates pagination inputs at the component edge. Returns true only when both
 * totalPages and currentPage are positive integers and currentPage is in range.
 */
function isValidPagination(currentPage: number, totalPages: number): boolean {
  return (
    Number.isInteger(totalPages) &&
    totalPages >= 1 &&
    Number.isInteger(currentPage) &&
    currentPage >= 1 &&
    currentPage <= totalPages
  );
}

/** Number of page buttons visible adjacent to the first/last page (excludes the anchor pages themselves). */
const VISIBLE_SIBLINGS = 2;

/** When totalPages is at or below this, every page number is shown without ellipsis. */
const MAX_PAGES_WITHOUT_ELLIPSIS = VISIBLE_SIBLINGS + 3; // first + siblings + ellipsis-slot + last

/** Pages at or below this index use the "near start" pattern. */
const NEAR_START_THRESHOLD = VISIBLE_SIBLINGS + 1;

/** Pages within this offset from the end use the "near end" pattern. */
const NEAR_END_OFFSET = VISIBLE_SIBLINGS;

/**
 * Calculates which page numbers to display with ellipsis.
 *
 * Patterns produced (with VISIBLE_SIBLINGS = 2, totalPages = 20):
 * - Near start (page 1-3):   [1, 2, 3, ..., 20]
 * - Middle     (page 10):    [1, ..., 10, ..., 20]
 * - Near end   (page 18-20): [1, ..., 18, 19, 20]
 */
function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= MAX_PAGES_WITHOUT_ELLIPSIS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  pages.push(1);

  if (currentPage <= NEAR_START_THRESHOLD) {
    for (let i = 2; i <= VISIBLE_SIBLINGS + 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
  } else if (currentPage >= totalPages - NEAR_END_OFFSET) {
    pages.push('ellipsis');
    for (let i = totalPages - VISIBLE_SIBLINGS; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push('ellipsis');
    pages.push(currentPage);
    pages.push('ellipsis');
  }

  pages.push(totalPages);
  return pages;
}

function getPageButtonSx(isActive: boolean): SxProps<Theme> {
  return {
    width: 36,
    height: 36,
    minWidth: 0,
    flexShrink: 1,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? 'primary.main' : 'text.secondary',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      color: isActive ? 'primary.dark' : 'text.primary',
    },
  };
}

/**
 * Pagination component for lists and tables.
 * Renders previous/next controls and page number buttons with ellipsis for large ranges.
 */
export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    { currentPage, totalPages, onPageChange, size = 'small', sx, 'data-testid': dataTestId },
    ref
  ) => {
    if (!isValidPagination(currentPage, totalPages)) {
      return null;
    }

    const handlePrevious = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNext = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    const handlePageClick = (page: number) => {
      if (page !== currentPage) {
        onPageChange(page);
      }
    };

    const pageNumbers = getPageNumbers(currentPage, totalPages);
    const showPrevious = currentPage > 1;
    const showNext = currentPage < totalPages;

    return (
      <Box
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        data-testid={dataTestId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          minWidth: 0,
          ...sx,
        }}
      >
        <IconButton
          onClick={handlePrevious}
          aria-label="Previous page"
          data-testid={dataTestId ? `${dataTestId}-previous` : 'pagination-previous'}
          size={size}
          sx={{
            flexShrink: 0,
            visibility: showPrevious ? 'visible' : 'hidden',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <Box
                key={`ellipsis-${index}`}
                aria-hidden="true"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  minWidth: 0,
                  flexShrink: 1,
                  height: 36,
                  color: 'text.secondary',
                  userSelect: 'none',
                }}
              >
                ...
              </Box>
            );
          }

          const isActive = page === currentPage;

          return (
            <IconButton
              key={page}
              onClick={() => handlePageClick(page)}
              aria-label={`Page ${page}`}
              aria-current={isActive ? 'page' : undefined}
              data-testid={dataTestId ? `${dataTestId}-page-${page}` : `pagination-page-${page}`}
              size={size}
              color={isActive ? 'primary' : 'default'}
              sx={getPageButtonSx(isActive)}
            >
              {page}
            </IconButton>
          );
        })}
        <IconButton
          onClick={handleNext}
          aria-label="Next page"
          data-testid={dataTestId ? `${dataTestId}-next` : 'pagination-next'}
          size={size}
          sx={{
            flexShrink: 0,
            visibility: showNext ? 'visible' : 'hidden',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    );
  }
);

Pagination.displayName = 'Pagination';
